import {
    Button,
    Checkbox,
    DialogActions,
    DialogTitle,
    FormControlLabel,
    Icon,
    IconButton,
    InputAdornment,
    Link,
    MenuItem,
    Select,
    TextField
} from "@material-ui/core";
import _ from "lodash";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
    GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS,
    IPresentation,
    IWorkspacePermissionAccess,
    IWorkspaceResourceFolder,
    IWorkspaceUserExtended,
    WorkspacePermissionSubjectType,
    WorkspaceResourceFolderType,
    WorkspaceResourceType,
    WorkspaceUserRole,
    ActionPermissionsObject,
    WorkspacePlanTier,
    PermissionActionType,
    IWorkspaceResource
} from "common/interfaces";
import getObjectHash from "common/utils/getObjectHash";
import WorkspaceController, {
    WorkspaceControllerState,
    getUserPermissionsToResourceFolder,
    getWorkspaceResourcesWithDocuments
} from "js/controllers/WorkspaceController";
import getLogger from "js/core/logger";
import { Key } from "js/core/utilities/keys";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { auth } from "js/firebase/auth";
import { UserProfile } from "js/react/components/Avatar";
import {
    BeautifulDialog,
    DialogContent,
    ShowConfirmationDialog,
    ShowDialog,
    ShowErrorDialog
} from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { FlexSpacer, Gap10, Gap20 } from "js/react/components/Gap";
import { createInviteSuggestionContext } from "js/react/components/InviteInput/InviteContext";
import { InviteSuggestions } from "js/react/components/InviteInput/InviteSuggestions";
import { FlexBox, GridBox, ScrollBox } from "js/react/components/LayoutGrid";
import { IconMenu } from "js/react/components/UiComponents";
import { MissingManageTeamFolderPermissionsDialog } from "js/react/views/TeamResources/dialogs/MissingManageTeamFolderPermissionsDialog";
import GuestBadge from "js/react/components/GuestBadge";
import { t } from "js/i18n";
import { UpgradeToTeamPlanDialog } from "js/react/components/Dialogs/UpgradeToTeamPlanDialog";

const logger = getLogger();

const NoMembers = styled.div`
    font-style: italic;
    color: #666;
`;

const MemberRow = styled.div.attrs({ "data-test-id": "team-folder-member-row" })`
    display: flex;
    width: 100%;
    button {
        opacity: 0;
    }
    &:hover {
        button {
        opacity: 1;
        }
    }
`;

const StyledLink = styled(Link)`
    font-size: 16px;
    font-weight: 500;
    font-family: "Source Sans Pro", sans-serif;
`;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 34px;
`;

const StyledOwnerCanEdit = styled.div`
    display: flex;
    flex-direction: row;
    gap: 3px;
`;

const FilterInput = styled(TextField)`
    .MuiOutlinedInput-adornedEnd {
        padding-right: 8px;
    }
    .MuiIcon-root {
        opacity: 0.33;
        cursor: pointer;
    }
`;

const HoverIconButton = styled(IconButton)`
    opacity: 0;
    transition: opacity 0.2s ease;
    
    ${MemberRow}:hover & {
        opacity: 1;
    }
`;

export interface AddPresentationsToFolderDialogProps {
    folderId: string;
    presentationIds: string[];
    closeDialog: () => void;
}

export const AddPresentationsToFolderDialog = WorkspaceController.withInitializedState(React.forwardRef(
    function AddPresentationsToFolderDialog(props: AddPresentationsToFolderDialogProps & WorkspaceControllerState, ref: React.Ref<any>) {
        const { folderId, presentationIds, resourceFolders, closeDialog } = props;
        const folder = resourceFolders.find(f => f.id === folderId);
        if (!folder) {
            throw new Error("Folder not found");
        }

        const [fetching, setFetching] = useState(false);
        const [presentationsRecords, setPresentationsRecords] = useState<{ permission: IWorkspacePermissionAccess, document: Pick<IPresentation, "id"> }[]>([]);
        const [presentationsPermission, setPresentationsPermission] = useState<"read" | "write">("write");

        useEffect(() => {
            (async () => {
                setFetching(true);
                try {
                    const { items } = await getWorkspaceResourcesWithDocuments({
                        offset: 0,
                        size: presentationIds.length,
                        workspaceId: WorkspaceController.workspaceId,
                        resourceTypes: [WorkspaceResourceType.PRESENTATION, WorkspaceResourceType.PRESENTATION_TEMPLATE],
                        resourceFilters: [{ field: "resourceId", operator: "in", value: presentationIds }],
                        documentFields: ["id"]
                    });
                    setPresentationsRecords(items);
                    setFetching(false);
                } catch (err) {
                    logger.error(err, "[AddPresentationsToFolderDialog] Failed to fetch folder permissions");
                }
            })();
        }, [folderId]);

        const usersWithAccess = WorkspaceController.getUsersWithAccessToResourceFolder(folderId);

        const shareablePresentationIds = presentationsRecords.filter(record =>
            record.permission.owner ||
            record.permission.write.object
        ).map(record => record.document.id);

        const handleChangePresentationsPermission = (event: React.ChangeEvent<{ value: "read" | "write" }>) => {
            setPresentationsPermission(event.target.value);
        };

        const handleOk = async () => {
            try {
                await withProgressDialog({
                    title: t("Sharing presentations"),
                    message: t("Please wait while we share the presentations..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderResources({
                        folderId,
                        update: {
                            add: shareablePresentationIds.map(id => ({
                                resourceId: id,
                                resourceType: WorkspaceResourceType.PRESENTATION,
                                permissions: {
                                    read: { object: true, permissions: true },
                                    write: { object: presentationsPermission === "write", permissions: presentationsPermission === "write" }
                                }
                            }))
                        }
                    })
                });
                closeDialog();
            } catch (err) {
                logger.error(err, "[AddPresentationsToFolderDialog] Failed to share presentations");

                ShowErrorDialog({
                    title: t("Failed to share presentations"),
                    message: t("Please try again later.")
                });
            }
        };

        const canUpgradeToUseSharedResources = WorkspaceController.plan.tier !== WorkspacePlanTier.BASIC && WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.USE);

        return (
            <BeautifulDialog closeDialog={closeDialog} ref={ref}>
                <FetchingClickShield visible={fetching} backgroundColor="#fff" />
                {canUpgradeToUseSharedResources && (
                    <UpgradeToTeamPlanDialog closeDialog={closeDialog} />
                )}
                {!canUpgradeToUseSharedResources && shareablePresentationIds.length > 0 && <>
                    <DialogTitle>
                        {shareablePresentationIds.length == 1
                            ? t("Are you sure you want to share this presentation with {{folderName}}?", { folderName: folder.name })
                            : t("Are you sure you want to share these presentations with {{folderName}}?", { folderName: folder.name })}
                    </DialogTitle>
                    <DialogContent>
                        <label>
                            {t("These team members will have")}
                            <Select value={presentationsPermission} onChange={handleChangePresentationsPermission} variant="outlined" style={{ margin: "0px 10px" }}>
                                <MenuItem value="read">{t("View")}</MenuItem>
                                <MenuItem value="write">{t("Edit")}</MenuItem>
                            </Select>
                            {t("access")}:
                        </label>
                        <Gap20 />
                        {/* @ts-ignore */}
                        <ScrollBox fillWidth style={{ maxHeight: 370 }}>
                            {/* @ts-ignore */}
                            <GridBox rows>
                                {usersWithAccess.map(member => (
                                    <UserProfile
                                        key={member.id}
                                        profile={{
                                            id: member.uid,
                                            email: member.email,
                                            displayName: member.displayName,
                                            photoURL: member.photoURL
                                        }}
                                    />
                                ))}
                            </GridBox>
                        </ScrollBox>
                    </DialogContent>
                    <DialogActions>
                        <Button
                            id="btnCancel"
                            onClick={closeDialog}
                        >{t("Cancel")}</Button>
                        <Button
                            id="btnOk"
                            color="primary"
                            onClick={handleOk}
                        >{t("OK")}</Button>
                    </DialogActions>
                </>}
                {!canUpgradeToUseSharedResources && shareablePresentationIds.length === 0 && <>
                    <DialogTitle>{t("View only presentations cannot be shared")}</DialogTitle>
                    <DialogActions>
                        <Button
                            id="btnCancel"
                            onClick={closeDialog}
                        >
                            {t("Close")}
                        </Button>
                    </DialogActions>
                </>}
            </BeautifulDialog >
        );
    }
));

export interface ManageTeamFolderMembersDialogProps {
    folderId: string;
    closeDialog: () => void;
    disableSettings?: boolean;
    disallowSelfRemoval?: boolean;
    viewOnly?: boolean;
}

export const ManageTeamFolderMembersDialog = WorkspaceController.withInitializedState(React.forwardRef(
    function ManageTeamFolderMembersDialog(props: ManageTeamFolderMembersDialogProps & WorkspaceControllerState, ref: React.Ref<any>) {
        const { folderId, resourceFolders, closeDialog, disableSettings, disallowSelfRemoval, users, viewOnly } = props;
        const folder = resourceFolders.find(f => f.id === folderId);
        if (!folder) {
            throw new Error("Folder not found");
        }
        if (folder.folderType === WorkspaceResourceFolderType.SHARED_DEFAULT && !viewOnly) {
            throw new Error("All team members folder only supports view only mode");
        }

        const [fetching, setFetching] = useState(false);
        const [userPermissionsToFolder, setUserPermissionsToFolder] = useState<IWorkspacePermissionAccess>(GET_DEFAULT_RESTRICTED_PERMISSIONS_ACCESS());
        const [onlyOwnerCanEditFetching, setOnlyOwnerCanEditFetching] = useState(false);

        useEffect(() => {
            (async () => {
                setFetching(true);
                try {
                    const userPermissions = await getUserPermissionsToResourceFolder({
                        workspaceId: folder.workspaceId,
                        folderId: folder.id
                    });
                    setUserPermissionsToFolder(userPermissions);
                    setFetching(false);
                } catch (err) {
                    logger.error(err, "[ManageTeamFolderMembersDialog] Failed to fetch folder permissions");
                }
            })();
        }, [getObjectHash(folder)]);

        const onlyOwnerCanEdit = !folder.permissionsOverrides?.[WorkspaceUserRole.MEMBER].write.permissions;
        const canManagePermissions = userPermissionsToFolder.write.permissions;
        const isFolderOwner = userPermissionsToFolder.owner;

        const members = WorkspaceController.getUsersWithAccessToResourceFolder(folderId);
        const nonMembers = users
            .filter(user => !members.some(member => member.id === user.id));

        const handleChangeOnlyOwnerCanEdit = async () => {
            try {
                setOnlyOwnerCanEditFetching(true);
                await WorkspaceController.updateWorkspaceResourceFolder({
                    folderId: folder.id,
                    permissionsOverrides: {
                        [WorkspaceUserRole.LIBRARIAN]: {
                            read: {
                                object: true,
                                permissions: true
                            },
                            write: {
                                object: true,
                                // Note: we're sending the flipped value here as we're
                                // handling checkox toggling
                                permissions: onlyOwnerCanEdit
                            },
                            owner: false
                        },
                        [WorkspaceUserRole.MEMBER]: {
                            read: {
                                object: true,
                                permissions: true
                            },
                            write: {
                                object: true,
                                // Note: we're sending the flipped value here as we're
                                // handling checkox toggling
                                permissions: onlyOwnerCanEdit
                            },
                            owner: false
                        }
                    }
                });
                setOnlyOwnerCanEditFetching(false);
            } catch (err) {
                logger.error(err, "[ManageTeamFolderMembersDialog] Failed to update only owner can edit");

                ShowErrorDialog({
                    title: t("Failed to update only owner can edit"),
                    message: t("Please try again later.")
                });
            }
        };

        // This adds all team members except guests
        const handleAddAllTeamMembers = async () => {
            try {
                await withProgressDialog({
                    title: t("Adding all team members"),
                    message: t("Please wait while we add all team members..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderUsers({
                        folderId: folder.id,
                        update: {
                            add: nonMembers
                                .filter(member => member.role !== WorkspaceUserRole.GUEST)
                                .map(member => ({
                                    subjectId: member.uid,
                                    subjectType: WorkspacePermissionSubjectType.USER
                                }))
                        }
                    })
                });
            } catch (err) {
                logger.error(err, "[ManageTeamFolderMembersDialog] Failed to add all team members");

                ShowErrorDialog({
                    title: t("Failed to add all team members"),
                    message: t("Please try again later.")
                });
            }
        };

        const handleRemoveAllTeamMembers = async () => {
            try {
                const confirmed = await ShowConfirmationDialog({
                    title: t("Are you sure you want to remove all the members (except yourself) from the folder?")
                });
                if (!confirmed) {
                    return;
                }

                await withProgressDialog({
                    title: t("Removing all team members"),
                    message: t("Please wait while we remove all team members..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderUsers({
                        folderId: folder.id,
                        update: {
                            remove: members
                                .filter(member => member.uid !== auth().currentUser.uid)
                                .map(member => ({
                                    subjectId: member.uid,
                                    subjectType: WorkspacePermissionSubjectType.USER
                                }))
                        }
                    })
                });
            } catch (err) {
                logger.error(err, "[ManageTeamFolderMembersDialog] Failed to remove all team members");

                ShowErrorDialog({
                    title: t("Failed to remove all team members"),
                    message: t("Please try again later.")
                });
            }
        };

        const handleShowMissingManageTeamFolderPermissionsDialog = () => {
            ShowDialog(MissingManageTeamFolderPermissionsDialog, {
                folderId: folder.id
            });
        };

        const handleRemoveMember = async (uid: string) => {
            try {
                if (uid === auth().currentUser.uid) {
                    const confirmed = await ShowConfirmationDialog({
                        title: t("Are you sure you want to remove yourself from this folder?")
                    });
                    if (!confirmed) {
                        return;
                    }
                }

                await withProgressDialog({
                    title: t("Removing team member"),
                    message: t("Please wait while we remove the team member..."),
                    action: () => WorkspaceController.updateWorkspaceResourceFolderUsers({
                        folderId: folder.id,
                        update: {
                            remove: [{
                                subjectId: uid,
                                subjectType: WorkspacePermissionSubjectType.USER
                            }]
                        }
                    })
                });
            } catch (err) {
                logger.error(err, "[ManageTeamFolderMembersDialog] Failed to remove team member");

                ShowErrorDialog({
                    title: t("Failed to remove team member"),
                    message: t("Please try again later.")
                });
            }
        };

        return (
            <BeautifulDialog closeDialog={closeDialog} ref={ref} data-test-id="manage-team-folder-members-dialog">
                <FetchingClickShield visible={fetching} backgroundColor="white" />
                <DialogTitle>
                    <FlexBox>
                        {t("Who can access {{folderName}}", { folderName: folder.name })}
                        <FlexSpacer />
                        {
                            !disableSettings &&
                            canManagePermissions &&
                            !viewOnly &&
                            // @ts-ignore
                            <IconMenu icon="settings">
                                {isFolderOwner &&
                                    <MenuItem divider>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    color="primary"
                                                    size="small"
                                                    checked={!onlyOwnerCanEdit}
                                                    onChange={handleChangeOnlyOwnerCanEdit}
                                                    disabled={onlyOwnerCanEditFetching}
                                                />
                                            }
                                            label={t("Allow members to manage folder permissions")}
                                        />
                                    </MenuItem>
                                }
                                {nonMembers.filter(member => member.role !== WorkspaceUserRole.GUEST).length > 0 &&
                                    <MenuItem onClick={handleAddAllTeamMembers}><Icon>group_add</Icon>{t("Add all team members to folder")}</MenuItem>
                                }
                                {members.length > 1 &&
                                    <MenuItem onClick={handleRemoveAllTeamMembers}><Icon>group_remove</Icon>{t("Remove everyone (but me) from folder")}</MenuItem>
                                }
                            </IconMenu>
                        }
                    </FlexBox>
                </DialogTitle>
                <DialogContent data-test-id="manage-team-folder-members-dialog-content">
                    <Container>
                        {!canManagePermissions && !viewOnly && <StyledOwnerCanEdit>
                            <span>{t("Reach out to a")}</span>
                            {/* @ts-ignore */}
                            <StyledLink component="button" onClick={handleShowMissingManageTeamFolderPermissionsDialog}>{t("team owner")}</StyledLink>
                            <span>{t("to add or remove members from this folder.")}</span>
                        </StyledOwnerCanEdit>}
                        <ScrollBox fillWidth style={{ maxHeight: "calc(25vh)" }}>
                            {/* @ts-ignore */}
                            <GridBox rows fillWidth>
                                {
                                    members.map(member => (
                                        <MemberRow key={member.id}>
                                            <UserProfile profile={member} />
                                            <FlexSpacer />
                                            {member.role === WorkspaceUserRole.GUEST && <GuestBadge />}
                                            {canManagePermissions && !viewOnly && members.length > 1 && (!disallowSelfRemoval || member.uid !== auth().currentUser.uid) &&
                                                <HoverIconButton onClick={() => handleRemoveMember(member.uid)}>
                                                    <Icon>close</Icon>
                                                </HoverIconButton>
                                            }
                                        </MemberRow>
                                    ))
                                }
                            </GridBox>
                            {members.length === 0 &&
                                <NoMembers>No team members can access this folder</NoMembers>
                            }
                        </ScrollBox>
                        {canManagePermissions && !viewOnly &&
                            <AddTeamMember folder={folder} members={members} nonMembers={nonMembers} />
                        }
                    </Container>
                </DialogContent>
            </BeautifulDialog >
        );
    }
));

function AddTeamMember(props: { folder: IWorkspaceResourceFolder, members: IWorkspaceUserExtended[], nonMembers: IWorkspaceUserExtended[] }) {
    const { folder, members, nonMembers } = props;
    const [inviteContext, setInviteContext] = useState<any>(null);

    useEffect(() => {
        if (nonMembers.length === 0) {
            setInviteContext(null);
            return;
        }

        const context = createInviteSuggestionContext({
            allSuggestions: [...members, ...nonMembers],
            selection: members,
            saveContext: ic => setInviteContext(_.cloneDeep(ic)),
        });
        setInviteContext(context);

        return () => context.destroy();
    }, [getObjectHash(folder), getObjectHash(members), getObjectHash(nonMembers)]);

    const handleSelect = async (uid: string) => {
        try {
            await withProgressDialog({
                title: t("Adding team member"),
                message: t("Please wait while we add the team member..."),
                action: () => WorkspaceController.updateWorkspaceResourceFolderUsers({
                    folderId: folder.id,
                    update: {
                        add: [{
                            subjectId: uid,
                            subjectType: WorkspacePermissionSubjectType.USER
                        }]
                    }
                })
            });
        } catch (err) {
            logger.error(err, "[AddTeamMember] Failed to add team member");

            ShowErrorDialog({
                title: t("Failed to add team member"),
                message: t("Please try again later.")
            });
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        switch (event.keyCode) {
            case Key.ENTER:
            case Key.UP_ARROW:
            case Key.DOWN_ARROW:
                // Prevent further events
                event.nativeEvent.stopImmediatePropagation();
                event.nativeEvent.preventDefault();

                switch (event.keyCode) {
                    case Key.ENTER:
                        const item = inviteContext.selectPendingSelection();
                        if (item) {
                            handleSelect(item.uid);
                        }
                        break;
                    case Key.UP_ARROW:
                        inviteContext.pendingSelectPrev();
                        break;
                    case Key.DOWN_ARROW:
                        inviteContext.pendingSelectNext();
                        break;
                }
                break;
        }
    };

    const handleChangeFilter = (event: React.ChangeEvent<HTMLInputElement>) => {
        inviteContext.setFilter(event.target.value);
    };

    const handleClearFilter = () => {
        inviteContext.setFilter("");
    };

    if (nonMembers.length === 0 || !inviteContext) {
        return null;
    }

    return (
        <FlexBox vertical left top fill>
            <FilterInput
                id="filter"
                fullWidth
                value={inviteContext.filter}
                onKeyDown={handleKeyDown}
                onChange={handleChangeFilter}
                variant="outlined"
                placeholder={t("Add team members to this folder")}
                autoFocus
                autoComplete="off"
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <Icon onClick={handleClearFilter}>close</Icon>
                        </InputAdornment>
                    )
                }}
            />
            <Gap10 />
            <InviteSuggestions
                inviteContext={inviteContext}
                onSelect={item => handleSelect(item.uid)}
                sourceName="ManageTeamFolderMembersDialog"
            />
        </FlexBox>
    );
}
